// In PatientScriptInfo.jsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import FormInput from "../../../components/FormInput";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import styles from "../css/patient-script-info.module.css";
import PatientFollowUpModal from "../../Dispensary/Script/views/FollowUp";
import { fetchFollowUp } from "../../../api/dispensary/script/processScriptApi.js";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Checkbox from "../../../components/Checkbox";
import { fetchScriptDetails, getReceiptByPrescriptionId } from "../../../api/dispensary/script/processScriptApi.js";
import { v4 as uuidv4 } from "uuid";

const scriptDetailsColumns = [
  { header: "Medication", accessor: "drugName" },
  { header: "Pack Size", accessor: "packSize" },
  { header: "ICD-10", accessor: "icd10" },
  { header: "Strength", accessor: "strength" },
  { header: "Qty", accessor: "quantity" },
  { header: "Nappi Code", accessor: "nappiCode" },
  { header: "Instructions", accessor: "instructions" },
  {
    header: "Cost Price",
    accessor: "costPrice",
    render: (value) => {
      if (value === null || value === undefined || value === "") return "R 0.00";
      const num = Number(value);
      if (Number.isNaN(num)) return `R ${value}`;
      return `R ${num.toFixed(2)}`;
    }
  },
];

export default function PatientScriptInfo({
  hideFields = [],
  extraFields = [],
  title = "Script Details",
  customButtons = null,
  showSelectColumn = false,
  selectedRows = {},
  onCheckboxChange = () => {},
  extraColumns = [], 
}) {
  const { scriptNo, scriptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [patientFollowUpModalOpen, setPatientFollowUpModalOpen] = useState(false);
  const [hasFollowUp, setHasFollowUp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [details, setDetails] = useState(null);
  
  // Get receipt number and totals from location state if available
  const receiptNumber = location.state?.receiptNumber || null;
  const totals = location.state?.totals || null;
  const barcodeValue = location.state?.barcodeValue || null;

  // Debug: Check what's in the location state
  console.log('[PatientScriptInfo] Location state:', location.state);
  console.log('[PatientScriptInfo] Receipt number:', receiptNumber);
  console.log('[PatientScriptInfo] Totals:', totals);
  console.log('[PatientScriptInfo] Barcode value:', barcodeValue);

  // Merge hideFields from props and location.state
  const mergedHideFields = [...hideFields, ...(location.state?.hideFields || [])];
  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const id = scriptId || scriptNo;
        if (!id) throw new Error("Missing script id");
        const data = await fetchScriptDetails(id);
        if (ignore) return;
        setDetails(data || null);
      } catch (e) {
        if (ignore) return;
        setError("Failed to load script details");
        setDetails(null);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [scriptNo, scriptId]);

  // When details load or change, check if follow-up exists
  useEffect(() => {
    let ignore = false;
    async function check() {
      const id = details?.script?.id;
      if (!id) return;
      try {
        const fu = await fetchFollowUp(id);
        if (ignore) return;
        setHasFollowUp(!!(fu && Array.isArray(fu.questions) && fu.questions.length));
      } catch (e) {
        if (!ignore) setHasFollowUp(false);
      }
    }
    check();
    return () => { ignore = true; };
  }, [details?.script?.id]);

  // Get the best script number to display (similar to chronic script view logic)
  const getScriptNumber = () => {
    return (
      details.script?.scriptNumber ||
      details.script?.scriptNo ||
      details.script?.processScriptNumber ||
      details.script?.processscriptnumber ||
      details.script?.clinicReference ||
      details.script?.clinicreference ||
      String(details.script?.id || "")
    );
  };

  // Handlers
  const handleMedicationLabel = async () => {
    console.log('[PatientScriptInfo] ========== MEDICATION LABEL CLICKED ==========');
    if (!details || !details.script) {
      console.log('[PatientScriptInfo] ERROR: No script details available');
      return;
    }

    const prescriptionId = details.script.id;
    console.log('[PatientScriptInfo] Prescription ID:', prescriptionId);

    // Extract ICD-10 codes from drugs
    const icd10Codes = (details.drugs || []).map(drug => drug.icd10).filter(Boolean);
    console.log('[PatientScriptInfo] Extracted ICD-10 codes:', icd10Codes);

    // Create label data structure
    const labelData = (details.drugs || []).map((drug, index) => ({
      id: uuidv4(),
      data: {
        patientName: details.patient ? `${details.patient.title || ''} ${details.patient.firstname || ''} ${details.patient.surname || ''}`.trim() : "",
        medication: drug.drugName || "",
        quantity: drug.quantity || "",
        prescriber: details.practitioner ? `${details.practitioner.title || ''} ${details.practitioner.firstname || ''} ${details.practitioner.surname || ''}`.trim() : "",
        rxNumber: details.script.processscriptnumber || details.script.scriptNumber || "",
        directions: drug.instructions || "",
        packSize: drug.packSize || "",
        icd10: icd10Codes[index] || (drug.icd10 || ""),
        practitioner: details.practitioner ? `${details.practitioner.title || ''} ${details.practitioner.firstname || ''} ${details.practitioner.surname || ''}`.trim() : "",
        barcode: "", // Will be populated from inventory search
        date: new Date().toISOString().split('T')[0],
      },
      isDrug: true,
    }));

    console.log('[PatientScriptInfo] Created label data:', labelData);

    // Create medications array for dropdown
    const medicationsArray = (details.drugs || []).map((drug, index) => ({
      id: `drug-${index}`,
      name: drug.drugName || '',
      quantity: drug.quantity || '',
      packSize: drug.packSize || '',
      barcode: "", // Will be populated from inventory search
      icd10Codes: icd10Codes[index] ? [icd10Codes[index]] : (drug.icd10 ? [drug.icd10] : [])
    }));

    console.log('[PatientScriptInfo] Created medications array:', medicationsArray);

    // Fetch receipt number from purchase table
    let fetchedReceiptNumber = receiptNumber; // Use existing receipt number from location state if available
    
    if (!fetchedReceiptNumber && prescriptionId) {
      try {
        console.log('[PatientScriptInfo] Fetching receipt number for prescription ID:', prescriptionId);
        const receiptResponse = await getReceiptByPrescriptionId(prescriptionId);
        if (receiptResponse.success && receiptResponse.data) {
          fetchedReceiptNumber = receiptResponse.data.receiptNumber;
          console.log('[PatientScriptInfo] Fetched receipt number:', fetchedReceiptNumber);
        } else {
          console.log('[PatientScriptInfo] No receipt number found for prescription:', prescriptionId);
        }
      } catch (error) {
        console.error('[PatientScriptInfo] Error fetching receipt number:', error);
      }
    }

    // Navigate to label configuration page with data
    const targetUrl = prescriptionId
      ? `/dispensary/label-configuration?prescriptionId=${prescriptionId}&icd10Codes=${icd10Codes.join(',')}`
      : `/dispensary/label-configuration?icd10Codes=${icd10Codes.join(',')}`;

    const navigationState = {
      labelData: labelData,
      medications: medicationsArray,
      scriptNumber: getScriptNumber(),
      pharmacyInfo: {
        name: "DURBAN NORTH PHY",
        address: "DOCTOR MEDICAL CENTRE35 UMHLANGA ROCKS DRIVE 0315637577",
        phone: "",
      },
      receiptNumber: fetchedReceiptNumber,
      totals: totals,
      barcodeValue: fetchedReceiptNumber
    };

    console.log('[PatientScriptInfo] ========== NAVIGATION DETAILS ==========');
    console.log('[PatientScriptInfo] Target URL:', targetUrl);
    console.log('[PatientScriptInfo] Navigation state being passed:', navigationState);
    console.log('[PatientScriptInfo] Receipt number in navigation state:', navigationState.receiptNumber);

    navigate(targetUrl, {
      state: navigationState
    });
    console.log('[PatientScriptInfo] Navigation executed successfully');
  };

  const handleDeliveryLabel = () => {
    alert("Generating Delivery Label for Script: " + (details?.script?.id || ""));
  };

  const handlePatientFollowUp = () => {
    setPatientFollowUpModalOpen(true);
  };

  // Patient details fields
  const patientName = details ? [details.patient?.title, details.patient?.firstname, details.patient?.surname].filter(Boolean).join(" ") : "";
  const practitionerName = details?.practitioner
    ? [details.practitioner?.title, details.practitioner?.firstname, details.practitioner?.surname].filter(Boolean).join(" ")
    : "";
  const pharmacistName = details?.pharmacist
    ? [details.pharmacist?.firstname, details.pharmacist?.surname].filter(Boolean).join(" ")
    : "";
  const baseFields = [
    { label: "Patient", key: "patient", value: patientName || "N/A", readOnly: true },
    { label: "Practitioner", key: "practitioner", value: practitionerName || "N/A", readOnly: true },
    { label: "Pharmacist", key: "pharmacist", value: pharmacistName || "N/A", readOnly: true },
    { label: "Payment Type", key: "paymentType", value: details?.script?.paymentType || "N/A", readOnly: true },
    { label: "Medical Aid", key: "medicalAid", value: details?.medicalAid?.provider || "N/A", readOnly: true },
    { label: "Medical Aid Number", key: "medicalAidNumber", value: details?.medicalAid?.number || "N/A", readOnly: true },
  ];

  // Filter out hidden fields
  const filteredFields = baseFields.filter((field) => !mergedHideFields.includes(field.key));

  // Insert extra fields before "Pharmacist"
  const pharmacistIndex = filteredFields.findIndex((field) => field.key === "pharmacist");
  const allFields = [
    ...filteredFields.slice(0, pharmacistIndex),
    ...extraFields,
    ...filteredFields.slice(pharmacistIndex),
  ];

  // ✅ Merge scriptDetailsColumns + extraColumns + select column
  const tableColumns = [
    ...scriptDetailsColumns,
    ...extraColumns,
    ...(showSelectColumn
      ? [
          {
            header: "Select",
            accessor: "select",
            render: (value, row, index) => (
              <Checkbox
                id={`checkbox-${details?.script?.id || ""}-${index}`}
                checked={selectedRows[index] || false}
                onChange={() => onCheckboxChange(index)}
                key={`checkbox-${details?.script?.id || ""}-${index}`}
              />
            ),
          },
        ]
      : []),
  ];

  // Default buttons if none passed
  const defaultButtons = [
    { label: "Medication Label", onClick: handleMedicationLabel, variant: "primary" },
    { label: "Delivery Label", onClick: handleDeliveryLabel, variant: "primary" },
    { label: hasFollowUp ? "View patient follow up" : "Patient Follow Up", onClick: handlePatientFollowUp, variant: "primary" },
  ];

  const buttonsToRender = customButtons || defaultButtons;

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.main}>
        <h2 className={styles.title}>{title}</h2>
        {loading && <p className={styles.loading}>Loading script details...</p>}
        {error && !loading && <p className={styles.loading}>{error}</p>}
        <div className={styles.formSection}>
          {/* Patient info fields */}
          <div className={styles.fields}>
            {allFields.map((field, index) => (
              <FormInput
                key={index}
                label={field.label}
                value={field.value}
                readOnly={field.readOnly}
              />
            ))}
          </div>

          {/* Drugs table */}
          <Table columns={tableColumns} data={(details?.drugs) || []} />

          {/* Buttons */}
          <div
            className={`${styles.buttonGroup} ${
              customButtons ? styles.centeredButtonGroup : ""
            }`}
          >
            {buttonsToRender.map((button, index) => (
              <Button
                key={index}
                variant={button.variant || "primary"}
                onClick={button.onClick}
                disabled={button.disabled || false}
              >
                {button.label}
              </Button>
            ))}
          </div>
        </div>
        {patientFollowUpModalOpen && (
          <PatientFollowUpModal
            onClose={() => setPatientFollowUpModalOpen(false)}
            scriptNumber={details?.script?.id || ""}
            onSaved={() => setHasFollowUp(true)}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}